import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown } from "lucide-react";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useInputLookup } from "../../hooks/useInputs";
import type { Input } from "../../services/inputsApi";

interface InputLookupProps {
  value: string;
  selectedInput?: Input | null;
  selectedLabel?: string;
  onSelect: (input: Input) => void;
  disabled?: boolean;
  placeholder?: string;
  activeOnly?: boolean;
  supplierId?: string;
  className?: string;
}

export function InputLookup({ value, selectedInput, selectedLabel, onSelect, disabled, placeholder = "Select input", activeOnly = false, supplierId, className }: InputLookupProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState<Input | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const query = useInputLookup(open && !disabled, debouncedSearch, supplierId);
  const waitingForSearch = search.trim() !== debouncedSearch;
  const inputs = waitingForSearch ? [] : query.data?.pages.flatMap((page) => page.items) ?? [];
  const label = (selectedInput?.id === value ? selectedInput : chosen?.id === value ? chosen : null);

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled} className={className ?? "w-full justify-between font-normal"}>
          <span className="truncate">{value ? selectedLabel || (label ? `${label.inputCode} — ${label.inputName}` : placeholder) : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search name, code, or variety..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{waitingForSearch || query.isFetching ? "Loading inputs..." : query.isError ? "Could not load inputs." : "No inputs found."}</CommandEmpty>
            <CommandGroup>
              {inputs.map((input) => (
                <CommandItem key={input.id} value={input.id} disabled={(activeOnly && !input.isActive) || (!!supplierId && input.supplierId !== supplierId)} onSelect={() => { setChosen(input); onSelect(input); setOpen(false); setSearch(""); }}>
                  {input.inputCode} — {input.inputName}
                </CommandItem>
              ))}
            </CommandGroup>
            {!waitingForSearch && query.hasNextPage && (
              <Button type="button" variant="ghost" className="w-full" disabled={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>
                {query.isFetchingNextPage ? "Loading..." : "Load more inputs"}
              </Button>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
